import { Link, useParams } from "react-router-dom";
import ProductGallery from "../../Components/modern/ProductGallery";
import VideoPreviewGrid from "../../Components/modern/VideoPreviewGrid";
import { Arrow, ButtonLink, Container, Eyebrow, SectionHeading } from "../../Components/modern/UI";
import { getProductById, products } from "../../Data/ProductData";

function SpecGrid({ specs }) {
  if (!specs.length) return null;
  return (
    <dl className="sn-overflow-hidden sn-rounded-card sn-border sn-border-line sn-bg-white">
      {specs.map((spec) => (
        <div key={spec.label} className="sn-grid sn-gap-2 sn-border-b sn-border-line sn-px-4 sn-py-3 last:sn-border-b-0 sm:sn-grid-cols-[0.4fr_0.6fr] sm:sn-items-start lg:sn-py-2.5">
          <dt className="sn-text-[11px] sn-font-bold sn-uppercase sn-tracking-[0.16em] sn-text-muted">{spec.label}</dt>
          <dd className="sn-text-[15px] sn-font-semibold sn-leading-snug sn-text-forest">{spec.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function BenefitsList({ benefits }) {
  if (!benefits.length) return null;
  return (
    <ul className="sn-grid sn-gap-3 sm:sn-grid-cols-2 lg:sn-grid-cols-5">
      {benefits.map((benefit) => (
        <li key={benefit} className="sn-rounded-card sn-border sn-border-line sn-bg-white sn-p-5 sn-text-sm sn-font-semibold sn-leading-relaxed sn-text-ink">
          {benefit}
        </li>
      ))}
    </ul>
  );
}

function getWhatsAppLink(productTitle) {
  const message = `Hello Suryanirbhar Agritech, I would like to enquire about the ${productTitle}.`;
  return `https://wa.me/919380226919?text=${encodeURIComponent(message)}`;
}

function galleryForProduct(product) {
  if (product.disableGalleryPlaceholders) return product.gallery;

  const placeholders = products
    .filter((item) => item.id !== product.id)
    .map((item) => ({
      src: item.pic[0],
      alt: `${product.title} temporary gallery placeholder using local product imagery`,
      temporary: true,
    }));
  return [...product.gallery, ...placeholders].slice(0, 4);
}

function ProductDetails() {
  const { productId } = useParams();
  const product = getProductById(productId);

  if (!product) {
    return (
      <section className="sn-py-20">
        <Container>
          <Eyebrow>Product not found</Eyebrow>
          <h1 className="sn-font-display sn-text-4xl sn-text-forest">We could not find that product.</h1>
          <div className="sn-mt-8">
            <ButtonLink to="/products">Back to products</ButtonLink>
          </div>
        </Container>
      </section>
    );
  }

  const whatsappUrl = getWhatsAppLink(product.title);
  const galleryImages = galleryForProduct(product);

  return (
    <>
      <section className="sn-border-b sn-border-line sn-py-4 md:sn-py-5">
        <Container>
          <Link to="/products" className="sn-inline-flex sn-min-h-[44px] sn-items-center sn-gap-3 sn-text-sm sn-font-semibold sn-text-forest hover:sn-text-leaf">
            <Arrow className="sn-h-4 sn-w-4 sn-rotate-180" />
            Back to products
          </Link>
        </Container>
      </section>

      <section className="sn-py-6 md:sn-py-8">
        <Container>
          <ProductGallery images={galleryImages} productName={product.title}>
            <div className="sn-w-full sn-min-w-0">
              <Eyebrow>{product.eyebrow}</Eyebrow>
              <h1 className="sn-font-display sn-text-4xl sn-leading-tight sn-tracking-[-0.04em] sn-text-forest md:sn-text-[44px]">{product.title}</h1>
              <p className="sn-mt-3 sn-text-base sn-leading-relaxed sn-text-muted md:sn-text-lg">{product.summary}</p>

              <div className="sn-mt-6">
                <h2 id={`sn-product-specs-${product.id}`} className="sn-font-display sn-text-[26px] sn-leading-tight sn-text-forest">Technical Specifications</h2>
                <div className="sn-mt-3">
                  <SpecGrid specs={product.specs} />
                </div>
              </div>
            </div>
          </ProductGallery>

          <div className="sn-mt-8 sn-border-t sn-border-line sn-pt-8 md:sn-mt-10 md:sn-pt-10">
            <SectionHeading eyebrow="Why Choose" id={`sn-product-benefits-${product.id}`}>Benefits</SectionHeading>
            <div className="sn-mt-6">
              <BenefitsList benefits={product.benefits} />
            </div>
          </div>
        </Container>
      </section>

      {product.videos.length > 0 && (
        <section aria-labelledby="sn-product-videos" className="sn-border-t sn-border-line sn-py-12 md:sn-py-16">
          <Container>
            <div className="sn-mb-8 sn-max-w-3xl">
              <SectionHeading eyebrow="Videos" id="sn-product-videos">Watch the machine in motion.</SectionHeading>
            </div>
            <VideoPreviewGrid videos={product.videos.map((video) => ({ ...video, heading: product.title, meta: "" }))} title={`${product.title} video`} />
          </Container>
        </section>
      )}

      <section className="sn-border-t sn-border-line sn-py-12 md:sn-py-16">
        <Container>
          <div className="sn-rounded-card sn-bg-forest sn-p-6 sn-text-cream md:sn-flex md:sn-items-center md:sn-justify-between md:sn-gap-8 md:sn-p-8">
            <div className="sn-max-w-2xl">
              <p className="sn-text-[11px] sn-font-bold sn-uppercase sn-tracking-[0.18em] sn-text-sage">Product enquiry</p>
              <h2 className="sn-mt-3 sn-font-display sn-text-3xl sn-leading-tight sn-text-white">Ask about {product.title} availability and suitability.</h2>
            </div>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="sn-mt-6 sn-inline-flex sn-min-h-[48px] sn-shrink-0 sn-items-center sn-justify-center sn-gap-4 sn-rounded sn-bg-cream sn-px-6 sn-py-3 sn-text-sm sn-font-semibold sn-text-forest sn-transition hover:sn-bg-white md:sn-mt-0"
            >
              Enquire Now
              <Arrow />
            </a>
          </div>
        </Container>
      </section>
    </>
  );
}

export default ProductDetails;
